import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../context/LanguageContext';

const { 
  FiCompass, FiMapPin, FiClock, FiDollarSign, FiCalendar, FiRefreshCw,
  FiCheckCircle, FiPlus, FiUsers, FiLock, FiExternalLink, FiSliders
} = FiIcons;

const PilotTab = () => {
  const { t } = useLanguage();
  const [viewMode, setViewMode] = useState('Week');
  const [selectedTier, setSelectedTier] = useState('Early Bird');

  const features = [
    {
      icon: FiCompass,
      title: t('solutionsPage.pilot.features.f1Title') || "Blueprint Your Vision",
      description: t('solutionsPage.pilot.features.f1Desc') || "Define event titles, rich descriptions, and secure private URLs. Your event's identity is established in seconds with our rapid-entry interface."
    },
    {
      icon: FiMapPin,
      title: t('solutionsPage.pilot.features.f2Title') || "Hybrid-Ready Infrastructure",
      description: t('solutionsPage.pilot.features.f2Desc') || "Seamlessly manage physical addresses or virtual access points. Pilot handles time zones and location mapping automatically for your guests."
    },
    {
      icon: FiClock,
      title: t('solutionsPage.pilot.features.f3Title') || "Master the Clock",
      description: t('solutionsPage.pilot.features.f3Desc') || "Whether it's a 2-hour workshop or a 5-day summit, our multi-day scheduling engine keeps your sessions organized and on track."
    },
    {
      icon: FiDollarSign,
      title: t('solutionsPage.pilot.features.f4Title') || "Flexible Revenue Flows",
      description: t('solutionsPage.pilot.features.f4Desc') || "Deploy group packages or individual ticketing with ease. Set capacity limits, early-bird pricing, and custom access tiers instantly."
    },
    {
      icon: FiCalendar,
      title: t('solutionsPage.pilot.features.f5Title') || "Panoramic Oversight",
      description: t('solutionsPage.pilot.features.f5Desc') || "Toggle between Month, Week, and List views. See the big picture or dive into the details without ever losing your place."
    },
    {
      icon: FiRefreshCw,
      title: t('solutionsPage.pilot.features.f6Title') || "Living Ecosystem",
      description: t('solutionsPage.pilot.features.f6Desc') || "Full bi-directional sync with Google Calendar. Changes made in Pilot reflect across your entire productivity suite in real-time."
    }
  ];

  const ticketTiers = [
    { name: 'Early Bird', price: '$149', cap: '250 / 250 Sold', status: 'Sold Out', color: 'emerald' },
    { name: 'General Access', price: '$249', cap: '612 / 800 Sold', status: 'Selling Fast', color: 'indigo' },
    { name: 'VIP Pass', price: '$599', cap: '88 / 100 Sold', status: 'Limited', color: 'purple' },
  ];

  const scheduleSlots = [
    { time: '09:00 AM', title: 'Global Keynote & Ecosystem Kickoff', track: 'Main Stage', type: 'Hybrid' },
    { time: '11:15 AM', title: 'Deep Dive: AI Concierge & Automation', track: 'Technical Track A', type: 'Virtual' },
    { time: '02:00 PM', title: 'Executive Networking & Partner Mixer', track: 'VIP Terrace', type: 'In-Person' },
    { time: '04:30 PM', title: 'Audience Q&A & Community Awards', track: 'Main Stage', type: 'Hybrid' },
  ];

  return (
    <div className="space-y-12 py-2">
      {/* Hero Header */}
      <div className="text-center max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-indigo-700 text-xs font-semibold uppercase tracking-wider shadow-sm inner-glow">
          <SafeIcon icon={FiCompass} className="w-3.5 h-3.5 text-indigo-600 animate-pulse" />
          <span>{t('solutionsPage.pilot.badge') || "Central Command For Logistics"}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          {t('solutionsPage.pilot.title') || "The Flight Deck for Your Events"}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          {t('solutionsPage.pilot.subtitle') || "The flight deck for your event. Manage schedules, tickets, and attendees from one command center."}
        </p>
      </div>

      {/* Interactive Flight Deck Preview */}
      <div className="glass-panel rounded-[28px] p-6 md:p-8 border border-white/80 shadow-xl shadow-slate-200/40 inner-glow relative overflow-hidden space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5 border-b border-slate-200/70">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">{t('solutionsPage.pilot.deckTitle') || "Pilot Flight Deck & Event Scheduler"}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">{t('solutionsPage.pilot.deckSubtitle') || "Real-time schedule synchronization with Google Calendar & ticketing engine."}</p>
          </div>
          
          {/* Panoramic View Selector */}
          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100/90 border border-slate-200/80 text-xs font-semibold">
            {['Month', 'Week', 'List'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  viewMode === mode
                    ? 'bg-white text-indigo-600 shadow-sm font-bold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {mode === 'Month' ? (t('solutionsPage.pilot.monthView') || 'Month View') : mode === 'Week' ? (t('solutionsPage.pilot.weekView') || 'Week View') : (t('solutionsPage.pilot.listView') || 'List View')}
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Flight Deck Grid */}
        <div className="grid lg:grid-cols-12 gap-6 items-start">
          {/* Schedule Engine (8 Cols) */}
          <div className="lg:col-span-8 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                {t('solutionsPage.pilot.schedulingEngine') || "Multi-Day Scheduling Engine"} ({viewMode === 'Month' ? (t('solutionsPage.pilot.monthView') || 'Month View') : viewMode === 'Week' ? (t('solutionsPage.pilot.weekView') || 'Week View') : (t('solutionsPage.pilot.listView') || 'List View')})
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                <SafeIcon icon={FiRefreshCw} className="w-3 h-3 animate-spin text-emerald-500" />
                {t('solutionsPage.pilot.syncActive') || "Bi-Directional Sync Active"}
              </span>
            </div>

            <div className="space-y-3">
              {scheduleSlots.map((slot, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.01, x: 2 }}
                  className="p-4 rounded-2xl bg-white/70 backdrop-blur-sm border border-slate-200/80 hover:border-indigo-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-all"
                >
                  <div className="flex items-start sm:items-center gap-3.5">
                    <div className="px-2.5 py-1.5 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-700 font-mono text-xs font-bold whitespace-nowrap">
                      {slot.time}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 leading-snug">{slot.title}</h4>
                      <p className="text-xs text-slate-500 font-medium mt-0.5">{slot.track}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                      slot.type === 'Hybrid' 
                        ? 'bg-purple-50 text-purple-700 border-purple-200' 
                        : slot.type === 'Virtual' 
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    }`}>
                      {slot.type}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Dynamic Ticketing & Revenue Flow (4 Cols) */}
          <div className="lg:col-span-4 space-y-4">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
              {t('solutionsPage.pilot.revenueTitle') || "Flexible Revenue Flows"}
            </span>
            <div className="space-y-3">
              {ticketTiers.map((tier) => {
                const isSelected = selectedTier === tier.name;
                return (
                  <div
                    key={tier.name}
                    onClick={() => setSelectedTier(tier.name)}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-white border-indigo-600 ring-2 ring-indigo-500/20 shadow-md'
                        : 'bg-white/60 border-slate-200/80 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-bold text-sm text-slate-900">{tier.name}</span>
                      <span className="text-base font-extrabold text-indigo-600">{tier.price}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{tier.cap}</span>
                      <span className={`font-semibold ${
                        tier.status === 'Sold Out' ? 'text-slate-400' : 'text-emerald-600'
                      }`}>
                        {tier.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 rounded-2xl bg-indigo-50/70 border border-indigo-100 text-xs text-indigo-900 space-y-1.5">
              <div className="flex items-center gap-1.5 font-bold text-indigo-700">
                <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5" />
                <span>{t('solutionsPage.pilot.gcalTitle') || "Google Calendar Sync Engine"}</span>
              </div>
              <p className="text-slate-600 leading-relaxed">
                {t('solutionsPage.pilot.gcalDesc') || "Changes in session titles or timing automatically update attendee Google Calendars without manual notifications."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* The 6 Core Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feat, idx) => (
          <motion.div
            key={idx}
            whileHover={{ y: -3 }}
            className="glass-card rounded-2xl p-6 border border-white/80 shadow-md shadow-slate-100/60 hover:shadow-xl hover:border-indigo-200/80 transition-all flex flex-col justify-between"
          >
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/10 to-indigo-600/20 border border-indigo-200/60 flex items-center justify-center text-indigo-600 shadow-sm">
                <SafeIcon icon={feat.icon} className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-900 tracking-tight">{feat.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-normal">{feat.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default PilotTab;
